import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { IcoLn } from '../shell/ActionIcons';

import { emoText } from '../shell/EmojiIcons';
export default function Combobox({
  options = [],
  value = '',
  onChange,
  placeholder,
  allowClear = true,
  disabled = false,
  id,
  name,
  style = {},   // merged into the root; pass { display:'block', width:'100%' } to fill the container
  onQueryChange,   // optional: the raw typed text (OpenOrderPicker's typed-order-id lookup)
}) {
  const { t } = useTranslation();
  const selected = options.find(o => o.value === value) ?? null;
  const _uid = useId();
  const listboxId = `${id || _uid}-listbox`;

  const [open, setOpen]       = useState(false);
  const [query, setQuery]     = useState('');
  const [highlight, setHighlight] = useState(0);

  const rootRef  = useRef(null);
  const inputRef = useRef(null);

  let filtered = query
    ? options.filter(o => (o.search ?? o.label).toLowerCase().includes(query.toLowerCase()))
    : options;

  // Grouped options: when any option carries a `group`, reorder the filtered list so
  // groups are contiguous (first-seen group order, original order kept inside each
  // group). Headings are rendered between groups; navigation/selection still runs
  // over the flat filtered list, so keyboard behaviour is unchanged.
  const grouped = options.some(o => o.group != null);
  if (grouped) {
    const byGroup = new Map();
    for (const o of filtered) {
      const g = o.group ?? 'Other';
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g).push(o);
    }
    filtered = [...byGroup.values()].flat();
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        closeWithoutChange();
      }
    }
    document.addEventListener('mousedown', handleDown);
    return () => document.removeEventListener('mousedown', handleDown);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function openList() {
    if (disabled) return;
    setQuery('');
    setHighlight(0);
    setOpen(true);
  }

  function closeWithoutChange() {
    setOpen(false);
    setQuery('');
  }

  function select(opt) {
    onChange(opt ? opt.value : '');
    setOpen(false);
    setQuery('');
  }

  function handleInputChange(e) {
    setQuery(e.target.value);
    if (onQueryChange) onQueryChange(e.target.value);
    setHighlight(0);
    if (!open) setOpen(true);
  }

  const handleKeyDown = useCallback((e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlight]) select(filtered[highlight]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeWithoutChange();
      inputRef.current?.blur();
    }
  }, [open, filtered, highlight]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayValue = open ? query : (selected?.label ?? '');
  // GES-253: the caller's placeholder still wins verbatim (it is the caller's data). Only the
  // built-in default moved into the catalog, and its English entry is the old default parameter
  // 'Select…' character-for-character, so no caller's rendering changes.
  const placeholderText = placeholder === undefined ? t('shared.combobox.placeholder') : placeholder;

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-block', minWidth: 160, ...style }}>
      {/* Trigger / input */}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          id={id}
          name={name}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          autoComplete="off"
          disabled={disabled}
          value={displayValue}
          placeholder={placeholderText}
          onChange={handleInputChange}
          onFocus={openList}
          onClick={open ? undefined : openList}
          onKeyDown={handleKeyDown}
          style={{
            padding: '6px 30px 6px 10px',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            fontSize: 13,
            width: '100%',
            boxSizing: 'border-box',
            background: disabled ? '#f1f5f9' : 'white',
            color: (!open && !selected) ? '#94a3b8' : '#0f172a',
            cursor: disabled ? 'not-allowed' : 'text',
            outline: 'none',
          }}
        />
        {/* Clear button */}
        {allowClear && selected && !disabled && !open && (
          <button
            type="button"
            tabIndex={-1}
            onClick={e => { e.stopPropagation(); select(null); }}
            style={S.clearBtn}
            aria-label={t('shared.combobox.clear')}
          ><IcoLn name="close" /></button>
        )}
        {/* Chevron */}
        {(!allowClear || !selected || open) && (
          <span style={{ ...S.chevron, transform: open ? 'rotate(180deg)' : undefined }}
            aria-hidden="true">▾</span>
        )}
      </div>

      {/* Dropdown list */}
      {open && (
        <ul
          id={listboxId}
          role="listbox"
          style={S.list}
        >
          {filtered.length === 0 && (
            <li style={{ ...S.option, color: '#94a3b8', cursor: 'default' }}>{t('shared.combobox.noMatches')}</li>
          )}
          {filtered.map((opt, idx) => (
            <React.Fragment key={opt.value}>
              {grouped && (idx === 0 || (filtered[idx - 1].group ?? 'Other') !== (opt.group ?? 'Other')) && (
                <li role="presentation" style={S.groupHeading}>{opt.group ?? 'Other'}</li>
              )}
              <li
                role="option"
                aria-selected={opt.value === value}
                onMouseDown={e => { e.preventDefault(); select(opt); }}
                onMouseEnter={() => setHighlight(idx)}
                style={{
                  ...S.option,
                  paddingLeft: grouped ? 20 : undefined,
                  background: idx === highlight ? '#eff6ff' : opt.value === value ? '#f0fdf4' : 'white',
                  fontWeight: opt.value === value ? 600 : 400,
                  color: opt.value === value ? '#15803d' : '#0f172a',
                }}
              >
                {emoText(opt.render ?? opt.label)}
              </li>
            </React.Fragment>
          ))}
        </ul>
      )}
    </div>
  );
}

const S = {
  clearBtn: {
    position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', fontSize: 16,
    color: '#94a3b8', lineHeight: 1, padding: '0 2px',
  },
  chevron: {
    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
    pointerEvents: 'none', fontSize: 11, color: '#94a3b8', transition: 'transform 0.15s',
  },
  list: {
    position: 'absolute', zIndex: 1000, top: 'calc(100% + 2px)', left: 0, right: 0,
    margin: 0, padding: '4px 0', listStyle: 'none',
    background: 'white', border: '1px solid #e2e8f0', borderRadius: 6,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    maxHeight: 220, overflowY: 'auto',
  },
  option: {
    padding: '7px 12px', fontSize: 13, cursor: 'pointer',
  },
  groupHeading: {
    padding: '6px 10px 2px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: 0.5, color: '#94a3b8', cursor: 'default',
  },
};
